import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import CheckIcon from '@material-ui/icons/Check';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import AccessLevelService, { gold } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import GiftsStore from 'stores/GiftsStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { GOLD_GIFT_CATEGORY } from 'enum/helpers';

import styles from './GoldGifts.css';


const GoldGifts = (props) => {
  const { canUse, category, upgradeToGold, isGiftsTab } = props;
  const { activeCategoryFilter, setCategoryFilter, getGiftByCategories } = GiftsStore;
  const { profileName } = ProfileStore;
  const { user } = UserStore;
  const { accessLevel } = user;

  const [anonymous, setAnonymous] = useState(false);

  const gift = category?.gifts?.[0] || {};
  const canSendGift = Boolean(canUse && !gift.alreadyUsed && gift.canUse);
  const isGold = AccessLevelService.isMin(gold, accessLevel);

  const sendGift = useCallback(() => {
    const payload = {
      recipientName: profileName,
      giftId: gift.id,
      price: null,
      message: '',
      anonymous,
    };
    GiftsStore.sendGift(payload);

    if (activeCategoryFilter === GOLD_GIFT_CATEGORY) {
      setCategoryFilter(null);
      getGiftByCategories();
    }
  }, [
    activeCategoryFilter,
    anonymous,
    getGiftByCategories,
    gift.id,
    profileName,
    setCategoryFilter,
  ]);

  const onToggleCheckbox = useCallback(() => {
    setAnonymous(!anonymous);
  }, [anonymous]);

  const wrapperClasses = classNames({
    [styles.goldGiftsContainer]: true,
    [styles.isGiftsTab]: isGiftsTab,
    [styles.giftAlreadySent]: isGiftsTab && gift.alreadyUsed,
  });

  let text = undefined;
  if (!canUse && isGold) {
    text = translate('gifting.profile.gift.available.after.trial.long');
  } else {
    text = translate(
      'v2profile.gold.exclusive.gift.text',
      { 0: '<strong>', 1: '</strong>' }
    );
  }

  return (
    <div className={wrapperClasses}>
      <div className={styles.goldGifts}>
        <div className={styles.image}>
          <img alt="gift" src={gift?.details?.imageUrl} />
          <div className={styles.giftName}>
            {translate(`gifting.${gift.ttKey}`, {}, gift.name)}
          </div>
        </div>
        <div>
          <p>{text}</p>
          {isGold && (
            <div className={styles.checkboxWrapper}>
              <CustomCheckbox
                checkboxClass={styles.check}
                checked={anonymous}
                className={styles.checkbox}
                disabled={!canSendGift}
                id="send-anonymous-gift"
                label={translate('v2profile.send.as.anonymous')}
                labelClass={styles.label}
                value="checkbox"
                onChange={onToggleCheckbox}
              />
              <Tooltip
                enterTouchDelay={0}
                title={translate('v2profile.anonymous.gift.tooltip')}
              >
                <InfoOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
          )}
          {gift.alreadyUsed && (
            <Button
              className={styles.giftSent}
              icon={<CheckIcon className={styles.checked} />}
              label={translate('v2profile.gift.sent.success.toast')}
            />
          )}
          {canSendGift && (
            <Button
              colour="orange"
              label={translate('v2profile.send.now')}
              onClick={sendGift}
            />
          )}
          {!canUse && !isGold && (
            <Button
              colour="green"
              label={translate('v2profile.upgrade.to.gold.button')}
              onClick={upgradeToGold}
            />
          )}
        </div>
      </div>
    </div>
  );
};

GoldGifts.propTypes = {
  upgradeToGold: PropTypes.func.isRequired,
  canUse: PropTypes.bool,
  category: PropTypes.object,
  isGiftsTab: PropTypes.bool,
};

GoldGifts.defaultProps = {
  canUse: false,
  category: undefined,
  isGiftsTab: false,
};

export default observer(GoldGifts);
